import React, { useState, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

interface UploadResponse {
  message: string;
  file: {
    originalName: string;
    filename: string;
    size: number;
    mimetype: string;
  };
  processing: any;
}

interface UploadHistoryItem {
  id: string;
  originalName: string;
  size: number;
  uploadedAt: string;
  status: 'success' | 'error';
  message?: string;
}

const FileUpload: React.FC = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadHistory, setUploadHistory] = useState<UploadHistoryItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // File validation
  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Excel 파일(.xlsx, .xls)만 허용됩니다';
    }

    if (file.size > maxSize) {
      return '파일 크기는 10MB 미만이어야 합니다';
    }

    return null;
  };

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadResponse> => {
      const formData = new FormData();
      formData.append('excel', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '업로드 실패');
      }

      return response.json();
    },
    onSuccess: (data, file) => {
      toast.success('파일이 성공적으로 업로드되었습니다!');

      // Add to upload history
      const historyItem: UploadHistoryItem = {
        id: Date.now().toString(),
        originalName: file.name,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        status: 'success',
        message: data.message,
      };

      setUploadHistory(prev => [historyItem, ...prev]);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
    },
    onError: (error: Error, file) => {
      toast.error(error.message || '업로드 실패');

      // Add to upload history
      const historyItem: UploadHistoryItem = {
        id: Date.now().toString(),
        originalName: file.name,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        status: 'error',
        message: error.message,
      };

      setUploadHistory(prev => [historyItem, ...prev]);
    },
  });

  // Handle file selection
  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file) return;

      const validationError = validateFile(file);

      if (validationError) {
        toast.error(validationError);
        return;
      }

      uploadMutation.mutate(file);
    },
    [uploadMutation]
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  // File input change handler
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
    },
    [handleFileSelect]
  );

  // Click to select file
  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Download template
  const handleDownloadTemplate = useCallback(() => {
    const link = document.createElement('a');
    link.href = '/api/template';
    link.download = 'territory-template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('템플릿 다운로드가 시작되었습니다!');
  }, []);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Template Download */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-blue-900 mb-1">
              Excel 템플릿 다운로드
            </h2>
            <p className="text-blue-700 text-sm">
              올바른 형식의 Excel 템플릿을 다운로드하여 작성 후 업로드하세요
            </p>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>템플릿 다운로드</span>
          </button>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer
          ${
            isDragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }
          ${uploadMutation.isPending ? 'opacity-50 pointer-events-none' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileInputChange}
          className="hidden"
          aria-label="Excel 파일 선택"
        />

        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 text-gray-400">
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Excel 파일 업로드
            </h2>
            <p className="text-gray-600 mb-4">
              {isDragOver
                ? '여기에 Excel 파일을 놓으세요'
                : 'Excel 파일을 여기에 끌어다 놓거나 클릭하여 선택하세요'}
            </p>
            <p className="text-sm text-gray-500">
              최대 10MB의 .xlsx 및 .xls 파일을 지원합니다
            </p>
          </div>

          {!uploadMutation.isPending && (
            <button
              type="button"
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              파일 선택
            </button>
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {uploadMutation.isPending && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            <span className="text-blue-700 font-medium">
              파일 업로드 및 처리 중...
            </span>
          </div>
          <div className="mt-3">
            <div className="bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full animate-pulse"
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Upload History */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          업로드 기록
        </h2>

        {uploadHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            아직 업로드된 파일이 없습니다
          </p>
        ) : (
          <div className="space-y-3">
            {uploadHistory.map(item => (
              <div
                key={item.id}
                className={`
                  flex items-center justify-between p-3 rounded-lg border
                  ${
                    item.status === 'success'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`
                    w-2 h-2 rounded-full
                    ${item.status === 'success' ? 'bg-green-500' : 'bg-red-500'}
                  `}
                  ></div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.originalName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {formatFileSize(item.size)} •{' '}
                      {formatDate(item.uploadedAt)}
                    </p>
                    {item.message && (
                      <p
                        className={`text-sm ${item.status === 'success' ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {item.message}
                      </p>
                    )}
                  </div>
                </div>

                <div
                  className={`
                  px-2 py-1 rounded text-xs font-medium
                  ${
                    item.status === 'success'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }
                `}
                >
                  {item.status === 'success' ? '성공' : '실패'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
